import { useMemo } from 'react';
import {
  Box,
  Select,
  MenuItem,
  FormControl,
  InputLabel,
  Chip,
  OutlinedInput,
  TextField,
  Autocomplete,
} from '@mui/material';
import {
  HourglassEmpty as PendingIcon,
  PlayArrow as InProgressIcon,
  CheckCircle as ConcludedIcon,
  Cancel as RejectedIcon,
} from '@mui/icons-material';
import { colors } from '@src/styles';
import { statusOptions, sourceOptions, typeOptions } from '../hooks/dto/taskItem.dto';
import { OrganizationAutocomplete } from './OrganizationAutocomplete';
import { useTasksContext } from '../context/TasksContext';
import { statusColors, statusLabels } from './StatusBar';

const statusIcons = {
  PENDING: PendingIcon,
  IN_PROGRESS: InProgressIcon,
  CONCLUDED: ConcludedIcon,
  REJECTED: RejectedIcon,
};

export const TaskFilters = () => {
  const {
    filters,
    teamMembers,
    organizationNames,
    handleFilterChange,
    messageFilter,
    handleMessageFilterChange,
    sourceFilter,
    handleSourceFilterChange,
    typeFilter,
    handleTypeFilterChange,
    statusFilter,
    handleStatusFilterChange,
    organizationFilter,
    handleOrganizationFilterChange,
    isLoadingOrganizations,
  } = useTasksContext();

  const allSourceOptions = useMemo(() => {
    return [...sourceOptions].sort();
  }, []);

  return (
    <Box sx={{ display: 'flex', gap: 1.5, flexWrap: 'wrap', alignItems: 'center' }}>
      <OrganizationAutocomplete
        organizations={organizationNames}
        isLoading={isLoadingOrganizations}
        value={organizationFilter}
        onChange={handleOrganizationFilterChange}
      />
      <TextField
        label="Nome"
        variant="outlined"
        size="small"
        value={messageFilter}
        onChange={(e) => handleMessageFilterChange(e.target.value)}
        sx={{
          minWidth: 200,
          '& .MuiInputLabel-root': {
            color: colors.primary.white,
            '&.Mui-focused': {
              color: colors.primary.yellow,
            },
          },
          '& .MuiOutlinedInput-root': {
            '& fieldset': {
              borderColor: colors.background.secondary,
              transition: 'border-color 0.2s ease',
            },
            '&:hover fieldset': {
              borderColor: colors.primary.yellow,
            },
            '&.Mui-focused fieldset': {
              borderColor: colors.primary.yellow,
            },
            '& input': {
              color: colors.primary.white,
            },
          },
        }}
        placeholder="..."
      />
      <FormControl variant="outlined" size="small" sx={{ width: 160 }}>
        <InputLabel sx={{ color: colors.primary.white, '&.Mui-focused': { color: colors.primary.yellow } }}>
          Status
        </InputLabel>
        <Select
          multiple
          value={statusFilter}
          onChange={(e) => handleStatusFilterChange(e.target.value as string[])}
          input={<OutlinedInput label="Status" />}
          renderValue={(selected) => (
            <Box
              sx={{
                display: 'flex',
                gap: 0.5,
                overflow: 'hidden',
                width: '100%',
                alignItems: 'center',
                height: '24px',
              }}
            >
              {selected.map((status) => {
                const Icon = statusIcons[status];
                return (
                  <Box
                    key={status}
                    sx={{
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      width: '20px',
                      height: '20px',
                      minWidth: '20px',
                      borderRadius: '50%',
                      backgroundColor: `${statusColors[status]}20`,
                      border: `1.5px solid ${statusColors[status]}`,
                    }}
                  >
                    <Icon
                      sx={{
                        color: statusColors[status],
                        fontSize: '12px',
                      }}
                    />
                  </Box>
                );
              })}
            </Box>
          )}
          sx={{
            color: colors.primary.white,
            '& .MuiOutlinedInput-notchedOutline': {
              borderColor: colors.background.secondary,
              transition: 'border-color 0.2s ease',
            },
            '&:hover .MuiOutlinedInput-notchedOutline': { borderColor: colors.primary.yellow },
            '&.Mui-focused .MuiOutlinedInput-notchedOutline': { borderColor: colors.primary.yellow },
            '& .MuiSelect-icon': { color: colors.primary.white },
          }}
          MenuProps={{
            PaperProps: {
              sx: {
                backgroundColor: colors.background.primary,
                border: `1px solid ${colors.background.secondary}`,
                '& .MuiMenuItem-root': {
                  color: `${colors.primary.white} !important`,
                  padding: '8px 12px',
                  minHeight: '44px',
                  border: '1px solid transparent',
                  transition: 'border-color 0.2s ease',
                  backgroundColor: 'transparent !important',
                  '&:hover': {
                    borderColor: colors.primary.yellow,
                  },
                  '&.Mui-selected': {
                    borderColor: colors.primary.yellow,
                  },
                },
              },
            },
          }}
        >
          {statusOptions.map((status) => {
            const Icon = statusIcons[status];
            const isSelected = statusFilter.includes(status);
            return (
              <MenuItem key={status} value={status}>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, width: '100%' }}>
                  <Box
                    sx={{
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      width: '28px',
                      height: '28px',
                      borderRadius: '50%',
                      backgroundColor: isSelected ? `${statusColors[status]}30` : `${statusColors[status]}15`,
                      border: `2px solid ${isSelected ? statusColors[status] : 'transparent'}`,
                      transition: 'all 0.15s ease',
                    }}
                  >
                    <Icon
                      sx={{
                        color: statusColors[status],
                        fontSize: '16px',
                      }}
                    />
                  </Box>
                  <Box
                    sx={{
                      color: colors.primary.white,
                      fontSize: '14px',
                      fontWeight: isSelected ? 600 : 400,
                    }}
                  >
                    {statusLabels[status] || status}
                  </Box>
                </Box>
              </MenuItem>
            );
          })}
        </Select>
      </FormControl>

      <FormControl variant="outlined" size="small" sx={{ width: 160 }}>
        <InputLabel sx={{ color: colors.primary.white, '&.Mui-focused': { color: colors.primary.yellow } }}>
          Fonte
        </InputLabel>
        <Select
          multiple
          value={sourceFilter}
          onChange={(e) => handleSourceFilterChange(e.target.value as string[])}
          input={<OutlinedInput label="Fonte" />}
          renderValue={(selected) => (
            <Box
              sx={{
                display: 'flex',
                gap: 0.5,
                overflow: 'hidden',
                width: '100%',
                alignItems: 'center',
              }}
            >
              {selected.length > 0 && (
                <>
                  <Chip
                    label={selected[0]}
                    size="small"
                    onDelete={(e) => {
                      e.stopPropagation();
                      const newSources = selected.filter((s) => s !== selected[0]);
                      handleSourceFilterChange(newSources);
                    }}
                    onMouseDown={(e) => e.stopPropagation()}
                    sx={{
                      height: '20px',
                      fontSize: '11px',
                      backgroundColor: colors.background.secondary,
                      color: colors.primary.white,
                      maxWidth: selected.length > 1 ? '120px' : '160px',
                      '& .MuiChip-label': {
                        overflow: 'hidden',
                        textOverflow: 'ellipsis',
                        whiteSpace: 'nowrap',
                      },
                      '& .MuiChip-deleteIcon': {
                        color: colors.primary.white,
                        fontSize: '14px',
                        '&:hover': { color: colors.primary.yellow },
                      },
                    }}
                  />
                  {selected.length > 1 && (
                    <Box
                      component="span"
                      sx={{
                        fontSize: '11px',
                        color: colors.primary.white,
                        opacity: 0.7,
                        fontWeight: 500,
                        whiteSpace: 'nowrap',
                      }}
                    >
                      +{selected.length - 1}
                    </Box>
                  )}
                </>
              )}
            </Box>
          )}
          sx={{
            color: colors.primary.white,
            '& .MuiOutlinedInput-notchedOutline': {
              borderColor: colors.background.secondary,
              transition: 'border-color 0.2s ease',
            },
            '&:hover .MuiOutlinedInput-notchedOutline': { borderColor: colors.primary.yellow },
            '&.Mui-focused .MuiOutlinedInput-notchedOutline': { borderColor: colors.primary.yellow },
            '& .MuiSvgIcon-root': { color: colors.primary.white },
          }}
          MenuProps={{
            PaperProps: {
              sx: {
                backgroundColor: colors.background.primary,
                border: `1px solid ${colors.background.secondary}`,
                '& .MuiMenuItem-root': {
                  color: `${colors.primary.white} !important`,
                  padding: '8px 12px',
                  minHeight: '44px',
                  fontSize: '14px',
                  border: '1px solid transparent',
                  transition: 'border-color 0.2s ease',
                  backgroundColor: 'transparent !important',
                  '&:hover': {
                    borderColor: colors.primary.yellow,
                  },
                  '&.Mui-selected': {
                    fontWeight: 600,
                    borderColor: colors.primary.yellow,
                  },
                },
              },
            },
          }}
        >
          {allSourceOptions.map((source) => (
            <MenuItem key={source} value={source}>
              {source}
            </MenuItem>
          ))}
        </Select>
      </FormControl>

      <FormControl variant="outlined" size="small" sx={{ width: 160 }}>
        <InputLabel sx={{ color: colors.primary.white, '&.Mui-focused': { color: colors.primary.yellow } }}>
          Tipo
        </InputLabel>
        <Select
          multiple
          value={typeFilter}
          onChange={(e) => handleTypeFilterChange(e.target.value as string[])}
          input={<OutlinedInput label="Tipo" />}
          renderValue={(selected) => (
            <Box
              sx={{
                display: 'flex',
                gap: 0.5,
                overflow: 'hidden',
                width: '100%',
                alignItems: 'center',
              }}
            >
              {selected.length > 0 && (
                <>
                  <Chip
                    label={selected[0]}
                    size="small"
                    onDelete={(e) => {
                      e.stopPropagation();
                      const newTypes = selected.filter((t) => t !== selected[0]);
                      handleTypeFilterChange(newTypes);
                    }}
                    onMouseDown={(e) => e.stopPropagation()}
                    sx={{
                      height: '20px',
                      fontSize: '11px',
                      backgroundColor: colors.background.secondary,
                      color: colors.primary.white,
                      maxWidth: selected.length > 1 ? '120px' : '160px',
                      '& .MuiChip-label': {
                        overflow: 'hidden',
                        textOverflow: 'ellipsis',
                        whiteSpace: 'nowrap',
                      },
                      '& .MuiChip-deleteIcon': {
                        color: colors.primary.white,
                        fontSize: '14px',
                        '&:hover': { color: colors.primary.yellow },
                      },
                    }}
                  />
                  {selected.length > 1 && (
                    <Box
                      component="span"
                      sx={{
                        fontSize: '11px',
                        color: colors.primary.white,
                        opacity: 0.7,
                        fontWeight: 500,
                        whiteSpace: 'nowrap',
                      }}
                    >
                      +{selected.length - 1}
                    </Box>
                  )}
                </>
              )}
            </Box>
          )}
          sx={{
            color: colors.primary.white,
            '& .MuiOutlinedInput-notchedOutline': {
              borderColor: colors.background.secondary,
              transition: 'border-color 0.2s ease',
            },
            '&:hover .MuiOutlinedInput-notchedOutline': { borderColor: colors.primary.yellow },
            '&.Mui-focused .MuiOutlinedInput-notchedOutline': { borderColor: colors.primary.yellow },
            '& .MuiSvgIcon-root': { color: colors.primary.white },
          }}
          MenuProps={{
            PaperProps: {
              sx: {
                backgroundColor: colors.background.primary,
                border: `1px solid ${colors.background.secondary}`,
                '& .MuiMenuItem-root': {
                  color: `${colors.primary.white} !important`,
                  padding: '8px 12px',
                  minHeight: '44px',
                  fontSize: '14px',
                  border: '1px solid transparent',
                  transition: 'border-color 0.2s ease',
                  backgroundColor: 'transparent !important',
                  '&:hover': {
                    borderColor: colors.primary.yellow,
                  },
                  '&.Mui-selected': {
                    fontWeight: 600,
                    borderColor: colors.primary.yellow,
                  },
                },
              },
            },
          }}
        >
          {typeOptions.map((type) => (
            <MenuItem key={type} value={type}>
              {type}
            </MenuItem>
          ))}
        </Select>
      </FormControl>

      <Autocomplete
        size="small"
        options={[
          { id: '', name: 'Todos usuários', isSpecial: true },
          { id: 'me_or_unassigned', name: 'Eu ou não atribuídas', isSpecial: true },
          { id: 'unassigned', name: 'Não atribuídos', isSpecial: true },
          ...teamMembers.sort((a, b) => a.name.localeCompare(b.name)).map((m) => ({ ...m, isSpecial: false })),
        ]}
        value={
          filters.assignedUserId
            ? [
                { id: '', name: 'Todos usuários', isSpecial: true },
                { id: 'me_or_unassigned', name: 'Eu ou não atribuídas', isSpecial: true },
                { id: 'unassigned', name: 'Não atribuídos', isSpecial: true },
                ...teamMembers.map((m) => ({ ...m, isSpecial: false })),
              ].find((opt) => opt.id === filters.assignedUserId) || null
            : null
        }
        onChange={(_, newValue) => handleFilterChange('assignedUserId', newValue?.id || '')}
        getOptionLabel={(option) => option.name}
        renderInput={(params) => (
          <TextField
            {...params}
            label="Responsável"
            sx={{
              minWidth: 170,
              '& .MuiInputLabel-root': {
                color: colors.primary.white,
                '&.Mui-focused': { color: colors.primary.yellow },
              },
              '& .MuiOutlinedInput-root': {
                '& fieldset': {
                  borderColor: colors.background.secondary,
                  transition: 'border-color 0.2s ease',
                },
                '&:hover fieldset': { borderColor: colors.primary.yellow },
                '&.Mui-focused fieldset': { borderColor: colors.primary.yellow },
                '& input': { color: colors.primary.white },
              },
              '& .MuiSvgIcon-root': { color: colors.primary.white },
            }}
          />
        )}
        renderOption={(props, option) => (
          <li
            {...props}
            style={{
              fontStyle: option.isSpecial ? 'italic' : 'normal',
            }}
          >
            {option.name}
          </li>
        )}
        sx={{
          '& .MuiAutocomplete-popupIndicator': { color: colors.primary.white },
          '& .MuiAutocomplete-clearIndicator': { color: colors.primary.white },
        }}
        slotProps={{
          paper: {
            sx: {
              backgroundColor: colors.background.primary,
              border: `1px solid ${colors.background.secondary}`,
              '& .MuiAutocomplete-listbox': {
                padding: 0,
                '& .MuiAutocomplete-option': {
                  color: colors.primary.white,
                  fontSize: '14px',
                  padding: '8px 12px',
                  minHeight: '44px',
                  border: '1px solid transparent',
                  transition: 'border-color 0.2s ease',
                  backgroundColor: 'transparent !important',
                  '&:hover': {
                    borderColor: colors.primary.yellow,
                  },
                  '&[aria-selected="true"]': {
                    fontWeight: 600,
                    borderColor: colors.primary.yellow,
                  },
                  '&.Mui-focused': {
                    borderColor: colors.primary.yellow,
                  },
                },
              },
            },
          },
        }}
      />
    </Box>
  );
};
